import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ErrorHandlingService {
    private readonly _translocoService = inject(TranslocoService);
    private _errorsLoaded = false;

    /**
     * Initialize error translations
     * Ensures error scope is loaded for all supported languages
     */
    async initializeErrorTranslations(): Promise<void> {
        if (this._errorsLoaded) {
            return;
        }

        const availableLangs = this._translocoService.getAvailableLangs() as { id: string; label: string }[];
        const loadPromises = availableLangs.map(lang => 
            firstValueFrom(this._translocoService.load(`errors/${lang.id}`))
        );

        try {
            await Promise.all(loadPromises);
            this._errorsLoaded = true;
        } catch (error) {
            console.warn('Failed to preload error translations:', error);
        }
    }

    /**
     * Get translated error message
     * Attempts to translate the error key, falls back to provided message if key doesn't exist
     */
    getErrorMessage(errorKey?: string, fallbackMessage?: string): string {
        if (!errorKey) {
            return fallbackMessage || this._translocoService.translate('errors.unexpected');
        }

        const translatedKey = this._translocoService.translate(`errors.${errorKey}`);
        
        // Check if translation exists (Transloco returns the key if translation not found)
        if (translatedKey !== `errors.${errorKey}`) {
            return translatedKey;
        }

        // Try without errors prefix in case it's already included
        const directTranslation = this._translocoService.translate(errorKey);
        if (directTranslation !== errorKey) {
            return directTranslation;
        }

        // Return fallback message or default error
        return fallbackMessage || this._translocoService.translate('errors.unexpected');
    }
}